import React, { useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { getGalleryByExpo, saveGallery } from "../Apis/GalleryAPIs";
import { uploadFile } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import { getExpoById, updateExpo } from "../Apis/ExpoAPIs";
import { getExpoBrands } from "../Apis/ExpoBrandAPIs";

const PastExpoGalleryLayer = () => {
  const { id: expoId } = useParams();
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [youtubeLinks, setYoutubeLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [newYoutubeUrl, setNewYoutubeUrl] = useState("");
  const [previewItem, setPreviewItem] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Brand mapping states
  const [expoDetails, setExpoDetails] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [selectedBrandOption, setSelectedBrandOption] = useState(null);
  const [mappedBrandLogos, setMappedBrandLogos] = useState([]);

  const fetchExpoAndBrands = async () => {
    setLoadingBrands(true);
    try {
      // 1. Fetch Expo Details
      const expoRes = await getExpoById(expoId);
      let currentBrandId = null;
      if (expoRes.status && expoRes.response) {
        const expoData = expoRes.response?.data || expoRes.response;
        setExpoDetails(expoData);
        if (expoData.expoBrandId) {
          currentBrandId = typeof expoData.expoBrandId === 'object' ? expoData.expoBrandId._id : expoData.expoBrandId;
        }
      }

      // 2. Fetch All Brands
      const brandsRes = await getExpoBrands();
      if (brandsRes.status) {
        const brandsData = brandsRes.response?.data || brandsRes.response || [];
        setBrands(brandsData);

        // Match selected brand option
        if (currentBrandId) {
          const matchedBrand = brandsData.find(b => (b._id || b.id) === currentBrandId);
          if (matchedBrand) {
            setSelectedBrandOption({
              value: matchedBrand._id || matchedBrand.id,
              label: matchedBrand.years && matchedBrand.years.length > 0
                ? `${matchedBrand.name} - (${matchedBrand.years.map(y => y.year).join(', ')})`
                : matchedBrand.name
            });
            setMappedBrandLogos(matchedBrand.logos || []);
          }
        } else {
          setSelectedBrandOption(null);
          setMappedBrandLogos([]);
        }
      }
    } catch (error) {
      console.error("Error loading mapping details:", error);
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleBrandChange = async (selectedOption) => {
    setLoadingBrands(true);
    try {
      const brandId = selectedOption ? selectedOption.value : null;
      const res = await updateExpo(expoId, { expoBrandId: brandId });
      if (res.status) {
        toast.success(brandId ? "Expo mapped to brand successfully!" : "Expo unmapped successfully!");
        setSelectedBrandOption(selectedOption);
        if (brandId) {
          const matchedBrand = brands.find(b => (b._id || b.id) === brandId);
          setMappedBrandLogos(matchedBrand ? matchedBrand.logos || [] : []);
        } else {
          setMappedBrandLogos([]);
        }
      } else {
        toast.error("Failed to map expo brand");
      }
    } catch (error) {
      console.error("Error mapping brand:", error);
      toast.error("An error occurred during mapping");
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchGallery = async () => {
    setLoading(true);
    const res = await getGalleryByExpo(expoId);
    if (res.status) {
      // The API returns { success: true, data: { allItems: [], grouped: {} } }
      let allItems = res.response?.data?.allItems || res.response?.data?.items || res.response?.items || (Array.isArray(res.response) ? res.response : []);
      
      if (!Array.isArray(allItems)) {
        allItems = [];
      }

      const fetchedImages = allItems.filter(i => i.type === "Image").map(i => ({ id: i._id || Math.random(), url: i.url, isSaved: true }));
      const fetchedVideos = allItems.filter(i => i.type === "Video").map(i => ({ id: i._id || Math.random(), url: i.url, isSaved: true }));
      const fetchedYoutube = allItems.filter(i => i.type === "YouTube" && i.url).map(i => {
        const videoId = extractYoutubeId(i.url);
        return { id: i._id || Math.random(), url: i.url, videoId, isSaved: true };
      });

      setImages(fetchedImages);
      setVideos(fetchedVideos);
      setYoutubeLinks(fetchedYoutube);
    }
    setLoading(false);
  };

  React.useEffect(() => {
    fetchGallery();
    fetchExpoAndBrands();
  }, [expoId]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      url: URL.createObjectURL(file),
      file: file,
    }));
    setImages([...images, ...newImages]);
    toast.success("Images added successfully");
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newVideos = files.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      url: URL.createObjectURL(file),
      file: file,
    }));
    setVideos([...videos, ...newVideos]);
    toast.success("Videos added successfully");
  };

  const handleAddYoutubeLink = () => {
    if (!newYoutubeUrl) return;
    const videoId = extractYoutubeId(newYoutubeUrl);
    if (!videoId) {
      toast.error("Invalid YouTube URL");
      return;
    }
    setYoutubeLinks([
      ...youtubeLinks,
      {
        id: Math.random().toString(36).substr(2, 9),
        url: newYoutubeUrl,
        videoId: videoId,
      },
    ]);
    setNewYoutubeUrl("");
    setShowYoutubeModal(false);
    toast.success("YouTube link added");
  };

  const extractYoutubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleSaveGallery = async () => {
    setLoading(true);
    try {
      const itemsToSave = [];

      // Process Images
      for (const img of images) {
        if (img.isSaved) {
          itemsToSave.push({ type: "Image", url: img.url });
        } else {
          const formData = new FormData();
          formData.append("file", img.file);
          formData.append("folder", "gallery");
          const uploadRes = await uploadFile(formData);
          if (uploadRes.status) {
            const path =
              uploadRes.response?.path ||
              uploadRes.response?.data?.path ||
              uploadRes.response;
            itemsToSave.push({ type: "Image", url: path });
          }
        }
      }

      // Process Videos
      for (const vid of videos) {
        if (vid.isSaved) {
          itemsToSave.push({ type: "Video", url: vid.url });
        } else {
          const formData = new FormData();
          formData.append("file", vid.file);
          formData.append("folder", "gallery");
          const uploadRes = await uploadFile(formData);
          if (uploadRes.status) {
            const path =
              uploadRes.response?.path ||
              uploadRes.response?.data?.path ||
              uploadRes.response;
            itemsToSave.push({ type: "Video", url: path });
          }
        }
      }

      // Process YouTube
      for (const yt of youtubeLinks) {
        itemsToSave.push({ type: "YouTube", url: yt.url });
      }

      const res = await saveGallery({ expoId, items: itemsToSave });
      if (res.status) {
        fetchGallery();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("An error occurred while saving gallery");
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = (type, id, e) => {
    e.stopPropagation();
    if (type === "image") setImages(images.filter((item) => item.id !== id));
    if (type === "video") setVideos(videos.filter((item) => item.id !== id));
    if (type === "youtube")
      setYoutubeLinks(youtubeLinks.filter((item) => item.id !== id));
    toast.info("Item removed from list (Save to persist changes)");
  };

  return (
    <div className="card h-100 p-0 radius-12 border-0 shadow-sm overflow-hidden">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(135deg, #000000, #bb0007, #ed1c24)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          
          <h6
            className="text-white mb-0 fw-bold uppercase tracking-wider"
            style={{ fontSize: "13px" }}
          >
            EXPO GALLERY MANAGEMENT
          </h6>
        </div>
        <Link 
          to="/past-expo" 
          className="btn btn-sm radius-8 px-16 py-8 fw-bold d-flex align-items-center gap-2 text-dark shadow-sm transition-all hover-scale-105"
          style={{ fontSize: '12px', backgroundColor: "#f7e7da" }}
        >
          <Icon icon="solar:arrow-left-bold" />
          Back to List
        </Link>
      </div>

      <div className="card-body p-16">
        {/* Select and Map Expo Brand Section */}
        <div className="mb-24 p-20 radius-12 bg-neutral-50 border shadow-sm">
          <div className="row align-items-center gy-3">
            <div className="col-md-5">
              <h6 className="text-dark fw-bold mb-4 d-flex align-items-center gap-2">
                <Icon icon="solar:link-bold" className="text-danger" />
                Select & Map Expo Brand
              </h6>
              <p className="text-xxs text-secondary-light m-0">
                Link this past expo to an Expo Brand to display mapped sponsor logos.
              </p>
            </div>
            <div className="col-md-7">
              <Select
                options={brands.map(b => ({
                  value: b._id || b.id,
                  label: b.years && b.years.length > 0
                    ? `${b.name} - (${b.years.map(y => y.year).join(', ')})`
                    : b.name
                }))}
                isSearchable={true}
                isLoading={loadingBrands}
                value={selectedBrandOption}
                onChange={handleBrandChange}
                styles={selectStyles()}
                placeholder="Search & Map Expo Brand (e.g. BUILD EXPO)..."
                isClearable={true}
              />
            </div>
          </div>

          {/* Mapped Logos Preview */}
          {mappedBrandLogos.length > 0 && (
            <div className="mt-20 pt-16 border-top">
              <label className="form-label text-xs fw-bold text-secondary-light uppercase mb-12">
                Mapped Brand Logos ({mappedBrandLogos.length}) - Managed in brand page
              </label>
              <div className="d-flex flex-wrap gap-12">
                {mappedBrandLogos.map((logo, idx) => (
                  <div
                    key={idx}
                    className="border radius-8 p-8 d-flex flex-column align-items-center bg-white shadow-sm"
                    style={{ width: "110px", height: "100px" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center w-100 overflow-hidden mb-6"
                      style={{ height: "50px" }}
                    >
                      <img
                        src={getImageUrl(logo.image)}
                        alt={logo.companyName || "logo"}
                        className="object-fit-contain w-100 h-100"
                      />
                    </div>
                    <span
                      className="text-xxs text-secondary text-center text-truncate w-100 fw-semibold px-4"
                      title={logo.companyName}
                    >
                      {logo.companyName}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Image Section */}
        <div className="mb-24">
          <div className="d-flex align-items-center justify-content-between mb-16 border-bottom pb-8">
            <h6 className="text-dark fw-bold mb-0 d-flex align-items-center gap-2">
              <Icon icon="solar:gallery-bold" className="text-danger" />
              Photo Gallery
            </h6>
          </div>
          <div className="d-flex flex-wrap gap-12 align-items-start">
            {/* Add Button */}
            <div
              className="add-item-card border-dashed radius-12 d-flex flex-column align-items-center justify-content-center cursor-pointer hover-bg-light transition-all"
              style={{
                width: "100px",
                height: "100px",
                border: "2px dashed #dee2e6",
              }}
              onClick={() => imageInputRef.current.click()}
            >
              <Icon
                icon="solar:add-circle-bold"
                className="text-3xl text-secondary-light"
              />
              <span className="text-xs fw-bold mt-8 text-secondary-light">
                Add Images
              </span>
              <input
                type="file"
                multiple
                hidden
                ref={imageInputRef}
                accept="image/*"
                onChange={handleImageUpload}
              />
            </div>

            {images.map((img) => (
              <div
                key={img.id}
                className="position-relative gallery-item-wrapper overflow-hidden radius-12 shadow-sm"
                style={{ width: "100px", height: "100px" }}
              >
                <img
                  src={img.isSaved ? getImageUrl(img.url) : img.url}
                  alt="Gallery"
                  className="w-100 h-100 object-fit-cover"
                />
                <div className="overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-60 opacity-0 transition-all hover-opacity-100 gap-2">
                  <button
                    className="btn-view-custom fw-medium w-36-px h-36-px d-flex justify-content-center align-items-center rounded-circle"
                    style={{
                      backgroundColor: "#eef2ff",
                      color: "#4338ca",
                      border: "1px solid #e0e7ff",
                    }}
                    onClick={() =>
                      setPreviewItem({ type: "image", url: img.isSaved ? getImageUrl(img.url) : img.url })
                    }
                  >
                    <Icon icon="ph:eye-bold" className="text-xl" />
                  </button>
                  <button
                    className="btn btn-sm btn-danger radius-8 p-8 shadow-sm d-flex align-items-center justify-content-center"
                    onClick={(e) => deleteItem("image", img.id, e)}
                  >
                    <Icon icon="ph:trash-bold" className="text-xl" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Video Section */}
        <div className="mb-24">
          <div className="d-flex align-items-center justify-content-between mb-16 border-bottom pb-8">
            <h6 className="text-dark fw-bold mb-0 d-flex align-items-center gap-2">
              <Icon icon="solar:video-library-bold" className="text-danger" />
              Video Gallery
            </h6>
          </div>
          <div className="d-flex flex-wrap gap-12 align-items-start">
            <div
              className="add-item-card border-dashed radius-12 d-flex flex-column align-items-center justify-content-center cursor-pointer hover-bg-light transition-all"
              style={{
                width: "140px",
                height: "80px",
                border: "2px dashed #dee2e6",
              }}
              onClick={() => videoInputRef.current.click()}
            >
              <Icon
                icon="solar:add-circle-bold"
                className="text-3xl text-secondary-light"
              />
              <span className="text-xs fw-bold mt-8 text-secondary-light">
                Add Videos
              </span>
              <input
                type="file"
                multiple
                hidden
                ref={videoInputRef}
                accept="video/*"
                onChange={handleVideoUpload}
              />
            </div>

            {videos.map((vid) => (
              <div
                key={vid.id}
                className="position-relative gallery-item-wrapper overflow-hidden radius-12 shadow-sm"
                style={{ width: "140px", height: "80px" }}
              >
                <video
                  src={vid.isSaved ? getImageUrl(vid.url) : vid.url}
                  className="w-100 h-100 object-fit-cover"
                  muted
                />
                <div className="overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-60 opacity-0 transition-all hover-opacity-100 gap-2">
                  <button
                    className="btn btn-sm bg-white text-dark radius-8 p-8 shadow-sm d-flex align-items-center justify-content-center"
                    onClick={() =>
                      setPreviewItem({ type: "video", url: vid.isSaved ? getImageUrl(vid.url) : vid.url })
                    }
                  >
                    <Icon icon="ph:play-bold" className="text-xl" />
                  </button>
                  <button
                    className="btn btn-sm btn-danger radius-8 p-8 shadow-sm d-flex align-items-center justify-content-center"
                    onClick={(e) => deleteItem("video", vid.id, e)}
                  >
                    <Icon icon="ph:trash-bold" className="text-xl" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* YouTube Section */}
        <div className="mb-24">
          <div className="d-flex align-items-center justify-content-between mb-16 border-bottom pb-8">
            <h6 className="text-dark fw-bold mb-0 d-flex align-items-center gap-2">
              <Icon icon="logos:youtube-icon" className="text-lg" />
              YouTube Links
            </h6>
          </div>
          <div className="d-flex flex-wrap gap-12 align-items-start">
            <div
              className="add-item-card border-dashed radius-12 d-flex flex-column align-items-center justify-content-center cursor-pointer hover-bg-light transition-all"
              style={{
                width: "140px",
                height: "80px",
                border: "2px dashed #dee2e6",
              }}
              onClick={() => setShowYoutubeModal(true)}
            >
              <Icon
                icon="solar:add-circle-bold"
                className="text-3xl text-secondary-light"
              />
              <span className="text-xs fw-bold mt-8 text-secondary-light">
                Add YouTube Link
              </span>
            </div>

            {youtubeLinks.map((yt) => (
              <div
                key={yt.id}
                className="position-relative gallery-item-wrapper overflow-hidden radius-12 shadow-sm"
                style={{ width: "140px", height: "80px" }}
              >
                <img
                  src={`https://img.youtube.com/vi/${yt.videoId}/mqdefault.jpg`}
                  alt="YT Preview"
                  className="w-100 h-100 object-fit-cover"
                />
                <div className="overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-60 opacity-0 transition-all hover-opacity-100 gap-2">
                  <button
                    className="btn btn-sm bg-white text-dark radius-8 p-8 shadow-sm d-flex align-items-center justify-content-center"
                    onClick={() =>
                      setPreviewItem({ type: "youtube", videoId: yt.videoId })
                    }
                  >
                    <Icon icon="ph:play-bold" className="text-xl" />
                  </button>
                  <button
                    className="btn btn-sm btn-danger radius-8 p-8 shadow-sm d-flex align-items-center justify-content-center"
                    onClick={(e) => deleteItem("youtube", yt.id, e)}
                  >
                    <Icon icon="ph:trash-bold" className="text-xl" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* YouTube Modal */}
      {showYoutubeModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 border-0 shadow-lg">
              <div className="modal-header border-bottom py-16 px-24 bg-light radius-top-16">
                <h6 className="modal-title fw-bold">Add YouTube Link</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowYoutubeModal(false)}
                ></button>
              </div>
              <div className="modal-body p-24">
                <div className="mb-16">
                  <label className="form-label fw-bold small text-secondary">
                    YouTube URL
                  </label>
                  <input
                    type="text"
                    className="form-control radius-8 border-neutral-200"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={newYoutubeUrl}
                    onChange={(e) => setNewYoutubeUrl(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <div className="modal-footer border-top p-16 bg-light radius-bottom-16">
                <button
                  className="btn btn-secondary radius-8 px-24 py-10 fw-bold"
                  onClick={() => setShowYoutubeModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger radius-8 px-24 py-10 fw-bold shadow-sm"
                  onClick={handleAddYoutubeLink}
                >
                  Add to Gallery
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.9)", zIndex: 1100 }}
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0 p-0 mb-16 justify-content-end">
                <button
                  type="button"
                  className="btn btn-white rounded-circle p-8 d-flex"
                  style={{ fontSize: "3rem" }}
                  onClick={() => setPreviewItem(null)}
                >
                  <Icon
                    icon="solar:close-circle-bold"
                    className="text-3xl text-danger"
                  />
                </button>
              </div>
              <div
                className="modal-body p-0 text-center d-flex align-items-center justify-content-center"
                style={{ minHeight: "60vh" }}
              >
                {previewItem.type === "image" && (
                  <img
                    src={previewItem.url}
                    alt="Preview"
                    className="img-fluid radius-16 shadow-lg"
                    style={{ maxHeight: "80vh" }}
                  />
                )}
                {previewItem.type === "video" && (
                  <video
                    src={previewItem.url}
                    controls
                    autoPlay
                    className="w-100 radius-16 shadow-lg"
                    style={{ maxHeight: "80vh" }}
                  />
                )}
                {previewItem.type === "youtube" && (
                  <div
                    className="w-100 ratio ratio-16x9 radius-16 overflow-hidden shadow-lg"
                    style={{ maxWidth: "1000px" }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${previewItem.videoId}?autoplay=1`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Persistent Footer Actions */}
      <div
        className="card-footer bg-light border-top py-24 px-24 d-flex justify-content-center gap-16 sticky-bottom"
        style={{ zIndex: 10, bottom: 0 }}
      >
        {/* <button
          onClick={fetchGallery}
          className="btn btn-lg btn-outline-secondary radius-12 px-32 fw-bold transition-all hover-bg-white shadow-sm"
          disabled={loading}
          style={{ height: "52px" }}
        >
          Discard Changes
        </button> */}
        <button
          onClick={handleSaveGallery}
          className="btn btn-lg btn-danger radius-12 px-48 fw-bold d-flex align-items-center gap-2 shadow-lg transition-all transform hover-scale-102"
          disabled={loading}
          style={{ height: "52px", backgroundColor: "#ed1c24" }}
        >
          {loading ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
            ></span>
          ) : (
            <>
              <Icon icon="solar:diskette-bold" className="text-xl" />
              Save Gallery Changes
            </>
          )}
        </button>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
                .hover-opacity-100:hover { opacity: 1 !important; }
                .gallery-item-wrapper .overlay { cursor: default; }
                .add-item-card:hover { border-color: #ed1c24 !important; }
                .add-item-card:hover span { color: #ed1c24 !important; }
                .add-item-card:hover svg { color: #ed1c24 !important; }
            `,
        }}
      />
    </div>
  );
};

export default PastExpoGalleryLayer;
